// /signin — magic-link request (round 3 §2). Four states: the form, "check
// your email", the expired-link state (?expired=1, from a failed
// /auth/callback), and the signed-out state (?signedout=1). No passwords
// anywhere. The response is the same whether or not the email is known.
import { useState } from 'react';
import { api, slugFromLocation } from '@/lib/api.js';
import Footer from '@/components/Footer.jsx';
import Wordmark from '@/components/Wordmark.jsx';

export default function Signin() {
  const params = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(params.get('email') || '');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const expired = params.get('expired') === '1' && !sent;
  const signedOut = params.get('signedout') === '1' && !sent && !expired;

  async function send() {
    setSending(true); setError(null);
    try {
      await api.post('/api/ctr/auth/request-link', { email, school_slug: slugFromLocation() });
      setSent(true);
    } catch (e) {
      setError(e.code === 'bad_email' ? 'That does not look like an email address.' : 'Something went sideways; try again in a moment.');
    } finally { setSending(false); }
  }

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <div className="mx-auto flex w-full max-w-[440px] flex-1 flex-col justify-center px-5 py-12">
        <a href="/" className="mb-6 block text-center text-xl text-primary"><Wordmark /></a>
        <div className="rounded-panel border border-line bg-white p-[30px_28px] shadow-card">
          {sent ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-pill bg-teal-fill text-[22px] text-accent">✉</div>
              <h1 className="mb-2 text-[20px] font-bold tracking-[-0.01em]">Check your email</h1>
              <p className="text-[13.5px] leading-[1.6] text-ink-dim">
                If <b className="text-ink-soft">{email}</b> has a dashboard, a sign-in link is on its way.
                It works once and expires in about an hour.
              </p>
              <button type="button" onClick={() => setSent(false)} className="mt-4 min-h-[44px] text-[13px] font-semibold text-accent hover:text-primary">
                Use a different address
              </button>
            </div>
          ) : (
            <>
              {expired ? (
                <div className="mb-4 rounded-card bg-warn-bg p-[12px_14px] text-[12.5px] leading-[1.55] text-warn-deep">
                  <b>That sign-in link has expired or was already used.</b> Send yourself another; it takes seconds.
                </div>
              ) : null}
              {signedOut ? (
                <div className="mb-4 rounded-card bg-teal-faint p-[12px_14px] text-[12.5px] leading-[1.55] text-teal-mute">
                  You&apos;re signed out. Sign back in any time with a fresh link.
                </div>
              ) : null}
              <h1 className="mb-1.5 text-[20px] font-bold tracking-[-0.01em]">Sign in to your dashboard</h1>
              <p className="mb-5 text-[13.5px] leading-[1.6] text-ink-dim">
                No passwords here. Enter the email on your school&apos;s account and we&apos;ll send you a one-time sign-in link.
              </p>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.04em] text-ink-soft">Email</label>
              <input
                type="email" autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder="you@yourschool.com"
                className="mb-3.5 min-h-[48px] w-full rounded-[9px] border-[1.5px] border-line p-[12px_14px] text-base outline-none focus:border-accent"
              />
              <button type="button" onClick={send} disabled={sending || !email}
                className="min-h-[50px] w-full rounded-card-lg bg-primary text-[15px] font-bold text-white hover:bg-primary-deep disabled:cursor-not-allowed disabled:opacity-60">
                {sending ? 'Sending…' : 'Send me a sign-in link'}
              </button>
              {error ? <div className="mt-2.5 text-center text-[12.5px] font-semibold text-warn-fg">{error}</div> : null}
              <div className="mt-4 text-center text-[12.5px] text-ink-faint">
                No dashboard yet? <a href="https://clearedto.rent/signup" className="font-semibold text-accent">Set up your school&apos;s portal</a>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
